import React, { useState, useRef, useEffect } from 'react';
import {
  DropdownContainer,
  DropdownButton,
  DropdownArrow,
  DropdownList,
  SearchInput,
  DropdownItem,
  Label,
  ClearButton
} from './styles';

interface DropdownOption {
  label: string;
  value: string;
}

interface CustomDropdownProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  showSearch?: boolean;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  showSearch = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(option => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current && showSearch) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelect = (option: DropdownOption) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <Label>{label}</Label>
      <DropdownButton
        isOpen={isOpen}
        hasValue={!!value}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', minHeight: '24px' }}>
          {value && (
            <div style={{ width: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClearButton onClick={handleClear} aria-label="Clear selection">
                ×
              </ClearButton>
            </div>
          )}
          <span style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            flex: 1,
            marginLeft: value ? '8px' : '0',
            lineHeight: '24px'
          }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <DropdownArrow isOpen={isOpen} />
        </div>
      </DropdownButton>
      
      <DropdownList isOpen={isOpen} role="listbox">
        {showSearch && (
          <SearchInput
            ref={searchInputRef}
            type="text"
            placeholder="Search options..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setHighlightedIndex(-1);
            }}
            onKeyDown={handleKeyDown}
          />
        )}
        {filteredOptions.map((option, index) => (
          <DropdownItem
            key={option.value}
            isSelected={option.value === value}
            isHighlighted={index === highlightedIndex}
            onClick={() => handleSelect(option)}
            role="option"
            aria-selected={option.value === value}
          >
            {option.label}
          </DropdownItem>
        ))}
        {filteredOptions.length === 0 && (
          <DropdownItem isSelected={false} isHighlighted={false}>
            No options found
          </DropdownItem>
        )}
      </DropdownList>
    </DropdownContainer>
  );
}; 